import { isFarmBasedPolicyType } from '@ourbranch/policy-types';

/**
 * Fee Selector eligibility helpers (issue #34828).
 *
 * `brandConfig` here is the staff-fe brand config object (the default export of
 * @ourbranch/brand-config / brand-config-fe), so the per-LOB fee amounts live at
 * `brandConfig.fees.{farm,home,auto}` — note this is the inner config object, NOT the
 * `{ config: { fees } }` shape @ourbranch/fee-utils generateFee expects.
 */

// Resolves the per-LOB fees block that governs a policy type (mirrors generateFee's getLobFeesConfig):
// farm for farm-based types (F/Z/Y), auto for Auto (A), home for Home (H).
export function getLobFees(policyType, brandConfig) {
  const fees = brandConfig?.fees;
  if (!fees) {
    return undefined;
  }
  if (isFarmBasedPolicyType(policyType)) {
    return fees.farm;
  }
  if (policyType === 'A') {
    return fees.auto;
  }
  if (policyType === 'H') {
    return fees.home;
  }
  return undefined;
}

// The brand-config-driven servicing fees (rewrite / NSF / late payment) are only offered in the Fee
// Selector when the brand configures an amount for this policy type's LOB — otherwise they would add
// as $0 for brands that don't charge them. All other fee types are always eligible here (their own
// state/policy-type rules are applied separately in the selector). For CFM: rewrite is auto-only,
// NSF is farm + auto, late payment is auto-only.
export function isServicingFeeEligible(feeId, policyType, brandConfig) {
  const lobFees = getLobFees(policyType, brandConfig);
  if (feeId === 'rewrite') {
    return lobFees?.rewritePolicyFee != null;
  }
  if (feeId === 'nsf') {
    return lobFees?.nsfFee != null;
  }
  if (feeId === 'late') {
    return lobFees?.latePaymentFee != null;
  }
  return true;
}

// Whether staff may add a fee of this type to a term that already has one. Only the NSF fee, and only where the
// brand's LOB fees set `allowMultipleNsfFees` (CFM: one NSF fee per failed payment, Joe 11/05). That covers an
// imported term carrying the legacy system's NSF line and a second NSF in a term. One per installment is staff's
// judgement here; the picker cannot tell which installment a failure belonged to.
export function isRepeatableFee(feeId, policyType, brandConfig) {
  return feeId === 'nsf' && getLobFees(policyType, brandConfig)?.allowMultipleNsfFees === true;
}

// The Fee Selector's options before its state rules: a type already on the policy is not offered again, except a
// repeatable one (isRepeatableFee), which stays on offer until one is added in this change; then the servicing-fee
// eligibility rules apply. `fees` are the store's fee lines (status 'pre-existing' / 'added' / 'removed').
export function getAvailableFeeOptions(feeOptions, fees, policyType, brandConfig) {
  return feeOptions.filter((option) => {
    const sameType = (fees ?? []).filter((fee) => fee.type === option.id);
    const alreadyApplied = isRepeatableFee(option.id, policyType, brandConfig)
      ? sameType.some((fee) => fee.status === 'added')
      : sameType.length > 0;
    return !alreadyApplied && isServicingFeeEligible(option.id, policyType, brandConfig);
  });
}
